import React from 'react';
import { withRouter } from 'react-router-dom';
import './LongTermScheduleCalendar.scss';
import moment from 'moment';
import authService from '../api-authorization/AuthorizeService';
import { util } from '../Util';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext from '../Common';
import { MomentDateFormat } from '../MomentDateFormat';
import { ApiRoutes } from '../ApiRoutes';
import LongTermScheduleForm from './LongTermScheduleForm';
import { LinearProgress } from '@mui/material';
import _ from 'lodash-es';
import LongTermScheduleToolbar from './LongTermScheduleToolbar';
import { computeDateRange } from './ScheduleDateMath';
import LtsCalendarGrid from './LtsCalendarGrid';
import { getTenantUserProfile } from '../common/TenantUserProfile';
import { AppNavPaths } from '../AppNavPaths';

const DEFAULT_SHIFT_ID = 2; // "All"

class LongTermScheduleCalendar extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.longTermScheduleForm = React.createRef();

        this.state = {
            events: [],
            user: null,
            calendarLongTermSchedules: [],
            crews: [],
            viewMode: '5week',
            customStart: moment().startOf('week').format('YYYY-MM-DD'),
            customEnd: moment().endOf('week').format('YYYY-MM-DD'),
            showLongTermScheduleForm: false,
            longTermScheduleDayCancelOptions: [],
            jobCancellationTypes: [],
            dayCancellations: [],
            date: new Date(),
            shiftId: DEFAULT_SHIFT_ID,
            ...new BaseFormViewModel(),
            unassignedCrewDetails: [],
            equipmentGroups: [],
            applicationGroups: [],
            employees: [],
            schedulers: [],
            equipmentTypes: [],
            divisions: [],
            ltsStateIds: [],
            days: [],
            searchFilter: '',
            canCreateLts: false,
            isAdmin: false,
            showAll: false,
            equipments: [],
        };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    onClearErrors = () => this.setState({ errors: {} });

    onNavigate = (direction) => {
        const { date, viewMode } = this.state;
        const m = moment(date);

        switch (viewMode) {
            case 'week':
                m.add(direction, 'weeks');
                break;
            case 'month':
                m.add(direction, 'months');
                break;
            case '5week':
                m.add(direction * 5, 'weeks');
                break;
            default:
                console.error(
                    `onNavigate - not sure what to do with viewMode [${viewMode}].`
                );
                break;
        }

        this.setState({ date: m.toDate() }, () => this.getLongTermSchedules());
    };

    goToday = () =>
        this.setState({ date: new Date() }, () => this.getLongTermSchedules());

    getLongTermSchedules = async () => {
        await this.setState({ loading: true });

        const {
            date,
            viewMode,
            customStart,
            customEnd,
            crews,
            contracts,
            divisions,
            divisionIds,
            shiftId,
            ltsStateIds,
            showAll,
            isAdmin,
        } = this.state;

        const { days, start, end } = computeDateRange(
            date,
            viewMode,
            customStart,
            customEnd
        );

        const startDate = start.format(MomentDateFormat.ApiUrlRequest);
        const endDate = end.format(MomentDateFormat.ApiUrlRequest);

        const { user } = this.context;
        const employeeId = user?.employeeId;

        const longTermScheduleQuery = {
            startDate,
            endDate,
            divisionIds: divisionIds || [],
            shiftId,
            ltsStateIds: ltsStateIds || [],
            showAll,
        };

        const [
            calendarLongTermSchedules,
            dayCancellations,
            unassignedCrewDetails,
        ] = await Promise.all([
            util.fetch.post(
                ApiRoutes.longTermSchedule.search(),
                longTermScheduleQuery
            ),
            util.fetch.post(
                ApiRoutes.longTermScheduleCancellation.search(),
                longTermScheduleQuery
            ),
            util.fetch.post(
                ApiRoutes.longTermSchedule.searchUnassignedCrews(),
                longTermScheduleQuery
            ),
        ]);

        const events = calendarLongTermSchedules.map((lts) => {
            const crew = crews.find((c) => lts.crewId == c.id) || {};
            const contract =
                contracts.find((c) => lts.contractId == c.id) || {};
            const division =
                (divisions || []).find((d) => d.id == crew.divisionId) || {};
            const crewName = lts.adHocCrewName || crew.name;

            return lts.workDays.map((workDay) => ({
                id: lts.id,
                crewId: lts.crewId,
                contractNumber: contract.number,
                crewName,
                divisionName: division.name || '',
                start: moment(workDay).toDate(),
                end: moment(workDay).toDate(),
                allDay: true,
                crewColorHex: crew.colorHex,
                divisionColorHex: division.colorHex,
                isAssigned: lts.isAssigned,
                isPublished: lts.isPublished,
                isNightShift: lts.isNightShift,
                eventDate: lts.isPublished ? workDay : null,
                isReleased: lts.releasedDays.some((d) =>
                    moment(workDay).isSame(d, 'day')
                ),
                isCancelled: dayCancellations.some(
                    (dc) =>
                        dc.longTermScheduleId == lts.id &&
                        moment(workDay).isSame(dc.date, 'day')
                ),
                showKeyIcon:
                    isAdmin ||
                    (lts.isAssigned &&
                        (lts.projectManagerId == employeeId ||
                            lts.superintendentId == employeeId)),
            }));
        });

        this.setState({
            events: _.flattenDeep(events) ?? [],
            loading: false,
            calendarLongTermSchedules,
            dayCancellations,
            startDate,
            endDate,
            unassignedCrewDetails,
            days,
        });
    };

    onViewChange = (e) => {
        const viewMode = e.viewMode;

        this.setState({ viewMode }, () => this.getLongTermSchedules());
    };

    onCustomStartChange = (e) =>
        this.setState({ customStart: e.target.value }, () =>
            this.getLongTermSchedules()
        );

    onCustomEndChange = (e) =>
        this.setState({ customEnd: e.target.value }, () =>
            this.getLongTermSchedules()
        );

    showLongTermScheduleForm = (event) => {
        this.context.setFormOpened(true);
        this.toggleLongTermScheduleForm(true);
        this.longTermScheduleForm.current.openLongTermSchedule(
            event.id,
            event.eventDate
        );
    };

    handleLongTermScheduleSelect = (event) => {
        const { isAdmin, calendarLongTermSchedules } = this.state;

        if (isAdmin) {
            this.showLongTermScheduleForm(event);

            return;
        }

        const longTermSchedule = calendarLongTermSchedules.find(
            (lts) => lts.id == event.id
        );

        const { employeeId } = this.context.user;

        if (
            !longTermSchedule ||
            !longTermSchedule.isAssigned ||
            (longTermSchedule.projectManagerId != employeeId &&
                longTermSchedule.superintendentId != employeeId)
        ) {
            return;
        }

        if (longTermSchedule.isPublished) {
            this.showLongTermScheduleForm(event);
        } else if (longTermSchedule.isAssigned) {
            const { history } = this.props;

            history.push(
                `${AppNavPaths.JobNewForLongTermSchedule}/${longTermSchedule.id}`
            );
        }
    };

    onDivisionChange = (selectedDivisions) => {
        this.setState({ divisionIds: selectedDivisions.map((d) => d.id) }, () =>
            this.getLongTermSchedules()
        );
    };

    onShiftChange = (e) => {
        const shiftId = e ? e.id : DEFAULT_SHIFT_ID;

        this.setState({ shiftId }, () => this.getLongTermSchedules());
    };

    onStateChange = (selectedStates) => {
        this.setState({ ltsStateIds: selectedStates.map((s) => s.value) }, () =>
            this.getLongTermSchedules()
        );
    };

    onShowAllChange = (e) => {
        this.setState({ showAll: e.target.checked }, () =>
            this.getLongTermSchedules()
        );
    };

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();

        if (!isAuthenticated) return;

        const [
            crews,
            contracts,
            companies,
            divisions,
            longTermScheduleDayCancelOptions,
            jobCancellationTypes,
            equipmentGroups,
            applicationGroups,
            employees,
            schedulers,
            equipmentTypes,
            userProfile,
            equipments,
            applicationUserGroups,
        ] = await Promise.all([
            util.fetch.js(ApiRoutes.crew.all()),
            util.fetch.js(ApiRoutes.contract.all()),
            util.fetch.js(ApiRoutes.company.all()),
            util.fetch.js(ApiRoutes.division.all()),
            util.fetch.js(ApiRoutes.longTermScheduleCancellation.getOptions()),
            util.fetch.js(ApiRoutes.typeAheads.jobCancellationTypes()),
            util.fetch.js(ApiRoutes.equipmentGroup.all()),
            util.fetch.js(ApiRoutes.userGroup.all()),
            util.fetch.js(ApiRoutes.employees.all()),
            util.fetch.js(ApiRoutes.employees.schedulers()),
            util.fetch.js(ApiRoutes.equipmentType.all()),
            getTenantUserProfile(),
            util.fetch.js(ApiRoutes.equipment.all()),
            util.fetch.js(ApiRoutes.applicationUserGroup.fieldUsers()),
        ]);

        const { userPermissions } = userProfile;

        const canCreateLts =
            userPermissions &&
            userPermissions.includes('longtermschedule.create');

        const isAdmin =
            userPermissions &&
            userPermissions.includes('longtermschedule.admin');

        this.setState(
            {
                crews,
                contracts,
                companies,
                divisions,
                longTermScheduleDayCancelOptions,
                jobCancellationTypes,
                equipmentGroups,
                applicationGroups,
                employees,
                schedulers,
                equipmentTypes,
                canCreateLts,
                equipments,
                isAdmin,
                applicationUserGroups,
            },
            () => {
                this.getLongTermSchedules();
            }
        );
    }

    onAddLongTermSchedule = () => {
        this.context.setFormOpened(true);
        this.setState({ showLongTermScheduleForm: true }, () =>
            this.longTermScheduleForm.current.openLongTermSchedule()
        );
    };

    toggleLongTermScheduleForm = (newValue) => {
        if (newValue) {
            this.setState({ showLongTermScheduleForm: newValue });
            return;
        }

        const { showLongTermScheduleForm } = this.state;
        this.setState({ showLongTermScheduleForm: !showLongTermScheduleForm });
    };

    onLongTermScheduleFormClosed = () => {
        this.context.setFormOpened(false);
        this.getLongTermSchedules();
    };

    onSearchFilterChange = (e) => {
        this.setState({ searchFilter: e.target.value });
    };

    userSet = () => Boolean((this.context ?? {}).user);

    render() {
        const { history } = this.props;
        const { showLongTermScheduleForm, loading } = this.state;

        if (!this.userSet()) {
            return null;
        }

        return (
            <div className="longTermScheduleCalendarPage">
                {loading && (
                    <LinearProgress variant="indeterminate" color="secondary" />
                )}

                <LongTermScheduleToolbar
                    {...this.state}
                    onViewChange={this.onViewChange}
                    onDivisionChange={this.onDivisionChange}
                    onShiftChange={this.onShiftChange}
                    onStateChange={this.onStateChange}
                    onCustomStartChange={this.onCustomStartChange}
                    onCustomEndChange={this.onCustomEndChange}
                    onAddLongTermSchedule={this.onAddLongTermSchedule}
                    onNavigate={this.onNavigate}
                    goToday={this.goToday}
                    onSearchFilterChange={this.onSearchFilterChange}
                    onShowAllChange={this.onShowAllChange}
                />

                <LtsCalendarGrid
                    {...this.state}
                    handleEventSelect={this.handleLongTermScheduleSelect}
                />

                <LongTermScheduleForm
                    show={showLongTermScheduleForm}
                    toggleShow={this.toggleLongTermScheduleForm}
                    ref={this.longTermScheduleForm}
                    onClose={this.onLongTermScheduleFormClosed}
                    {...this.state}
                    history={history}
                />
            </div>
        );
    }
}

export default withRouter(LongTermScheduleCalendar);
